// 교정/교열 요청 결과 캐시 유틸리티

/**
 * 캐시 키 생성 함수
 * @param {string} text - 원본 텍스트
 * @param {string} mode - 모드 (proofread/copyedit)
 * @param {boolean} includereasons - 이유 포함 여부
 * @param {string} language - 언어
 * @param {string} userApiKey - 사용자 API 키
 * @returns {string} 캐시 키
 */
const generateCacheKey = (text, mode, includereasons, language, userApiKey) => {
  // 텍스트를 정규화하여 공백 차이 무시
  const normalizedText = text.trim().replace(/\s+/g, ' ');
  
  // 해시 생성 (간단한 문자열 해시)
  const hashString = `${normalizedText}|${mode}|${includereasons}|${language}|${userApiKey || 'guest'}`;
  
  // 간단한 해시 함수
  let hash = 0;
  for (let i = 0; i < hashString.length; i++) {
    const char = hashString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // 32비트 정수로 변환
  }
  
  return `otj_cache_${Math.abs(hash)}`;
};

/**
 * 캐시에서 결과 조회
 * @param {string} text - 원본 텍스트
 * @param {string} mode - 모드 (proofread/copyedit)
 * @param {boolean} includereasons - 이유 포함 여부
 * @param {string} language - 언어
 * @param {string} userApiKey - 사용자 API 키
 * @returns {Object|null} 캐시된 결과 또는 null
 */
export const getCachedResult = (text, mode, includereasons, language, userApiKey) => {
  try {
    const cacheKey = generateCacheKey(text, mode, includereasons, language, userApiKey);
    const cachedData = localStorage.getItem(cacheKey);
    
    if (cachedData) {
      const parsed = JSON.parse(cachedData);
      
      // 캐시 만료 확인 (24시간)
      const now = Date.now();
      const cacheAge = now - parsed.timestamp;
      const maxAge = 24 * 60 * 60 * 1000; // 24시간
      
      if (cacheAge < maxAge) {
        console.log('[Cache] 캐시에서 결과 반환:', cacheKey);
        return parsed.data;
      } else {
        // 만료된 캐시 삭제
        localStorage.removeItem(cacheKey);
        console.log('[Cache] 만료된 캐시 삭제:', cacheKey);
      }
    }
    
    console.log('[Cache] 캐시 미스:', cacheKey);
    return null;
  } catch (error) {
    console.error('[Cache] 캐시 조회 오류:', error);
    return null;
  }
};

/**
 * 결과를 캐시에 저장
 * @param {string} text - 원본 텍스트
 * @param {string} mode - 모드 (proofread/copyedit)
 * @param {boolean} includereasons - 이유 포함 여부
 * @param {string} language - 언어
 * @param {string} userApiKey - 사용자 API 키
 * @param {Object} result - 저장할 결과 데이터
 */
export const setCachedResult = (text, mode, includereasons, language, userApiKey, result) => {
  try {
    const cacheKey = generateCacheKey(text, mode, includereasons, language, userApiKey);
    const cacheData = {
      timestamp: Date.now(),
      data: result
    };
    
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    console.log('[Cache] 결과 캐시 저장:', cacheKey);
    
    // 캐시 크기 관리 (최대 100개 항목 유지)
    cleanupCache();
  } catch (error) {
    console.error('[Cache] 캐시 저장 오류:', error);
  }
};

/**
 * 오래된 캐시 항목 정리
 */
const cleanupCache = () => {
  try {
    const cacheKeys = [];
    
    // localStorage에서 캐시 키들 찾기
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('otj_cache_')) {
        cacheKeys.push(key);
      }
    }
    
    // 100개 이상이면 오래된 것부터 삭제
    if (cacheKeys.length > 100) {
      const itemsWithTimestamp = cacheKeys.map(key => {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          return { key, timestamp: data.timestamp };
        } catch {
          return { key, timestamp: 0 };
        }
      });
      
      // 타임스탬프 기준 정렬 (오래된 것부터)
      itemsWithTimestamp.sort((a, b) => a.timestamp - b.timestamp);
      
      // 오래된 것부터 삭제해서 50개만 유지
      const toDelete = itemsWithTimestamp.slice(0, cacheKeys.length - 50);
      toDelete.forEach(item => {
        localStorage.removeItem(item.key);
      });
      
      console.log(`[Cache] 정리 완료: ${toDelete.length}개 항목 삭제`);
    }
  } catch (error) {
    console.error('[Cache] 캐시 정리 오류:', error);
  }
};

/**
 * 캐시 전체 삭제
 */
export const clearCache = () => {
  try {
    const keysToDelete = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('otj_cache_')) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => localStorage.removeItem(key));
    console.log(`[Cache] 전체 캐시 삭제: ${keysToDelete.length}개 항목`);
  } catch (error) {
    console.error('[Cache] 캐시 삭제 오류:', error);
  }
}; 